import React, { useEffect, useState } from "react";
import { Avatar, Button, Form, Input, Select, message, Upload } from "antd";
import type { RcFile } from "antd/es/upload/interface";
import axios from "axios";
import { apiUrl } from "@/config/urls";
import { copyObjects } from "../../util/copyObjects";
import {
  useAllAffiliationCodes,
  useAllCommunityCodes,
} from "@/hooks/useCommunityCodes";
import {
  clickHereDraggerStyles,
  headerDraggerStyles,
  subtitleDraggerStyles,
} from "./styles";
import Colors from "../../config/Colors";
import { CustomIcons } from "../../assets";

type EditCompanyProps = {
  editCompanyValues?: IEditCompany;
  onFormFinish: (values: IEditCompany) => Promise<void>;
  onModalClose: () => void;
  isLoading: boolean;
  isSuccess: boolean;
};

const EditCompany = ({
  editCompanyValues,
  onFormFinish,
  onModalClose,
  isLoading,
  isSuccess,
}: EditCompanyProps) => {
  const [form] = Form.useForm();

  // State management
  const [imageUrl, setImageUrl] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [communityCode, setCommunityCode] = useState<string>("");
  const [referralCode, setReferralCode] = useState<string>("");

  // API hooks
  const communityCodes = useAllCommunityCodes();
  const referralCodes = useAllAffiliationCodes();

  // Get the current company image URL
  const getCurrentImageUrl = () => {
    if (imageUrl) return imageUrl; // Newly uploaded image
    if (editCompanyValues?.companyImage) return editCompanyValues.companyImage; // Existing image
    return ""; // No image
  };

  // Handle image upload
  const handleImageUpload = async (file: RcFile): Promise<string | false> => {
    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/svg+xml",
      "image/gif",
    ];
    if (!validTypes.includes(file.type)) {
      message.error("You can only upload JPG/PNG/SVG/GIF files!");
      return false;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      message.error("Image must be smaller than 10MB!");
      return false;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${apiUrl}/util/post/document`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-API-Version": "1.0",
            "X-API-Client": "SaferCity",
            "X-Platform": "Web",
            "X-Tenant": "SaferCity",
          },
        }
      );

      if (response.data?.path) {
        message.success("Image uploaded successfully!");
        return response.data.path;
      } else {
        message.error("Upload succeeded but response format is invalid");
        return false;
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to upload image");
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Handle before upload
  const beforeUpload = (file: RcFile) => {
    handleImageUpload(file).then((path) => {
      if (path) {
        setImageUrl(path);
        form.setFieldsValue({ companyImage: path });
      }
    });
    return false; // Prevent automatic upload
  };

  // Handle form submission
  const onFinish = async (formValues: IEditCompany) => {
    const values: IEditCompany = copyObjects(editCompanyValues, formValues);

    values.companyImage = getCurrentImageUrl();

    if (communityCode && communityCode !== "") {
      values.communityCode = communityCode;
    }

    if (referralCode && referralCode !== "") {
      values.referralCode = referralCode;
    }

    await onFormFinish(values);
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    setImageUrl("");
    setCommunityCode("");
    setReferralCode("");
    setUploading(false);
    onModalClose();
  };

  // Handle community code change
  const handleChangeCommunityCode = (value: string) => {
    setCommunityCode(value);
  };

  // Handle referral code change
  const handleChangeReferralCode = (value: string) => {
    setReferralCode(value);
  };

  // Filter options for select components
  const filterOption = (input: string, option?: any) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  // Initialize form when editCompanyValues changes - following EditUser pattern
  useEffect(() => {
    console.log(
      "useEffect triggered with editCompanyValues:",
      editCompanyValues
    );
    if (editCompanyValues) {
      // Clean up "N/A" values and set form fields
      const cleanedValues = {
        ...editCompanyValues,
        communityCode:
          editCompanyValues.communityCode === "N/A"
            ? ""
            : editCompanyValues.communityCode,
        referralCode:
          editCompanyValues.referralCode === "N/A"
            ? ""
            : editCompanyValues.referralCode,
      };

      form.setFieldsValue(cleanedValues);

      setImageUrl("");
      setCommunityCode("");
      setReferralCode("");
    } else {
      form.resetFields();
      setImageUrl("");
    }
  }, [editCompanyValues, form]);

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  return (
    <div>
      <Form
        form={form}
        name="editCompany"
        layout="vertical"
        onFinish={onFinish}
        initialValues={editCompanyValues}
      >
        {/* Company Name */}
        <div className="col-12">
          <Form.Item
            name="companyName"
            label="Name of Company"
            rules={[{ required: true, message: "Please enter company name!" }]}
            className="mb-1"
          >
            <Input size="large" placeholder="ABC Ltd" />
          </Form.Item>
        </div>

        {/* Community Code */}
        <div className="col-12">
          {editCompanyValues?.communityCode &&
          editCompanyValues.communityCode !== "N/A" ? (
            <Form.Item label="Community Code" className="mb-1">
              <Input
                size="large"
                value={editCompanyValues.communityCode}
                readOnly
                className="bg-gray-50"
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="communityCode"
              label="Community Code"
              className="mb-1"
            >
              <Select
                showSearch
                size="large"
                filterOption={filterOption}
                placeholder="Search to select a Community Code"
                loading={communityCodes.isLoading}
                allowClear
              >
                {communityCodes.data?.map((item: IAllCommunityCode) => (
                  <Select.Option
                    key={`community-code-${item.id}`}
                    value={item.code}
                  >
                    {item.code}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </div>

        {/* Referral Code */}
        <div className="col-12">
          {editCompanyValues?.referralCode &&
          editCompanyValues.referralCode !== "N/A" ? (
            <Form.Item label="Referral Code" className="mb-1">
              <Input
                size="large"
                value={editCompanyValues.referralCode}
                readOnly
                className="bg-gray-50"
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="referralCode"
              label="Referral Code"
              className="mb-1"
            >
              <Select
                showSearch
                size="large"
                filterOption={filterOption}
                placeholder="Search to select a Referral Code"
                loading={referralCodes.isLoading}
                allowClear
              >
                {referralCodes.data?.map((item: IAllAffiliationCode) => (
                  <Select.Option
                    key={`referral-code-${item.id}`}
                    value={item.code}
                  >
                    {item.code}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </div>

        {/* Company Image */}
        <Form.Item
          name="companyImage"
          label="Company Icon"
          rules={[
            { required: true, message: "Please upload company icon!" },
            () => ({
              validator() {
                const currentImage = getCurrentImageUrl();
                if (!currentImage || currentImage === "") {
                  return Promise.reject(
                    new Error("Please upload a valid image.")
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
          className="mb-5 col-12"
        >
          <div className="flex w-full gap-6 flex-row justify-start items-start">
            {/* Image Preview */}
            <div className="flex-shrink-0">
              <Avatar
                shape="square"
                size={64}
                alt="company icon"
                src={getCurrentImageUrl()}
                className="border border-gray-200"
              >
                {!getCurrentImageUrl() && (
                  <CustomIcons.UploadIcon
                    width={24}
                    height={24}
                    svgColor={Colors.subtleTextColor}
                  />
                )}
              </Avatar>
            </div>

            {/* Upload Area */}
            <Upload.Dragger
              name="file"
              multiple={false}
              beforeUpload={beforeUpload}
              showUploadList={false}
              className="flex-1"
              style={{ background: "transparent" }}
              accept=".svg,.png,.jpg,.jpeg,.gif"
              disabled={uploading}
            >
              <div className="flex flex-col items-center justify-center w-full h-full gap-3 py-8">
                <CustomIcons.UploadIcon
                  width={46}
                  height={46}
                  svgColor={
                    uploading ? Colors.subtleTextColor : Colors.appMainColor
                  }
                />

                <div className="flex gap-1 justify-center flex-wrap">
                  <div className="text-center" style={headerDraggerStyles}>
                    {uploading ? "Uploading..." : "Drop your icon here or"}
                  </div>
                  {!uploading && (
                    <>
                      <div style={clickHereDraggerStyles}>click here</div>
                      <div className="text-center" style={headerDraggerStyles}>
                        to upload
                      </div>
                    </>
                  )}
                </div>

                <div style={subtitleDraggerStyles}>
                  File should be SVG, PNG, JPG or GIF (max 10MB)
                </div>
              </div>
            </Upload.Dragger>
          </div>
        </Form.Item>

        {/* Action Buttons */}
        <Form.Item className="w-full pt-4 mb-1">
          <div className="flex w-full gap-3 justify-end">
            <Button
              onClick={handleClose}
              type="default"
              size="large"
              className="min-w-24"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              type="primary"
              size="large"
              loading={isLoading}
              className="min-w-24 bg-[#1757a2]"
              disabled={uploading}
            >
              Save Changes
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EditCompany;
